/**
 * useSelectionKeys — keyboard parity for the selection bar's verbs.
 *
 * The bar is the visible path; these are the recall shortcuts every editor
 * user already expects (Nielsen's "flexibility and efficiency"): Delete /
 * Backspace removes the selection, Escape clears it, Shift+Enter selects the
 * section around a widget. None of them existed — Escape only cancelled a
 * live gesture and delete was drag-to-trash only.
 *
 * Guards: nothing fires while a text field, contenteditable or the grid's own
 * keyboard grab has the key (the grid item stops propagation for the keys it
 * owns, and text widgets stop Escape while editing — so a window listener
 * only ever sees the leftovers, which is exactly the set we want).
 */
import { useEffect } from "react";
import { useGridStore } from "@fredericrous/grid";

import type { SelectionView } from "./selectionView.js";

/** Keys typed inside a settings surface (the Inspector, a quick-edit card,
 *  the on-canvas overlay controls) belong to that surface: Backspace on one of
 *  its buttons must not delete the widget, Escape must not clear the
 *  selection out from under it. */
function inSettingsSurface(t: EventTarget | null): boolean {
  return (
    t instanceof Element && t.closest("[data-wb-settings-surface]") !== null
  );
}

function inTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function deleteSelection(view: SelectionView): void {
  const st = useGridStore.getState();
  if (
    view.kind === "widget" ||
    view.kind === "section" ||
    view.kind === "inline"
  ) {
    st.removeItem(view.gridId, view.itemId);
  } else if (view.kind === "multi") {
    for (const id of view.itemIds) st.removeItem(view.gridId, id);
  } else {
    return;
  }
  st.clearSelection();
}

export function selectParent(view: SelectionView): void {
  const parent =
    view.kind === "widget" || view.kind === "multi" ? view.parent : null;
  if (!parent) return;
  useGridStore.getState().setSelection(parent.gridId, [parent.itemId]);
}

export function useSelectionKeys(view: SelectionView): void {
  useEffect(() => {
    if (view.kind === "none") return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        inTextTarget(e.target) ||
        inSettingsSurface(e.target)
      )
        return;
      // A live pointer/keyboard gesture owns Escape (the grid cancels it).
      const busy = useGridStore.getState().interaction.gesture.kind !== "idle";
      if (e.key === "Escape") {
        if (busy) return;
        e.preventDefault();
        useGridStore.getState().clearSelection();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (busy || e.metaKey || e.ctrlKey || e.altKey) return;
        e.preventDefault();
        deleteSelection(view);
        return;
      }
      if (e.key === "Enter" && e.shiftKey) {
        if (busy) return;
        e.preventDefault();
        selectParent(view);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);
}
